import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchAppWelcomeWizardStatus,
  postCreateWelcomeWizardCampaign,
} from "../../Services/redux/Welcome-wizard/WelcomeWizardActions";

const getPortfolioPrefix = () =>
  sessionStorage.getItem("portfolioId") === "TML"
    ? "TATA"
    : sessionStorage.getItem("portfolioId") || "";

const mapEcuTypeInclusions = (selectedTypes, portfolioPrefix) =>
  selectedTypes.map((type) => `${portfolioPrefix}_${type}`);

const getAppsStatusMessage = (appsData) => {
  if (appsData === undefined) {
    return "Loading apps...";
  }
  if (appsData.length === 0) {
    return "No apps found";
  }
  return "No apps match your search";
};

const renderAppsSelectionContent = ({
  isAppSearchEnabled,
  filteredApps,
  searchApp,
  selectedApps,
  handleAppToggle,
  appsData,
}) => {
  if (!isAppSearchEnabled) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography sx={{ color: "#888", fontSize: 14 }}>
          Select Model, ECU Type, Software Version and Countries to enable app
          selection
        </Typography>
      </Box>
    );
  }

  if (filteredApps.length > 0) {
    return (
      <List dense>
        {filteredApps
          .filter((app) =>
            app.title.toLowerCase().includes(searchApp.toLowerCase())
          )
          .map((app) => (
            <ListItem
              key={app.packageId}
              button
              onClick={() => handleAppToggle(app, true)}
              sx={{ py: 0.5 }}
              disabled={
                !selectedApps.includes(app.packageId) && selectedApps.length >= 8
              }
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox
                  edge="start"
                  checked={selectedApps.includes(app.packageId)}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                />
              </ListItemIcon>
              <ListItemText primary={app.title} />
            </ListItem>
          ))}
      </List>
    );
  }

  return (
    <Box sx={{ p: 2, textAlign: "center" }}>
      <Typography sx={{ color: "#888", fontSize: 14 }}>
        {getAppsStatusMessage(appsData)}
      </Typography>
    </Box>
  );
};

const renderYearSelectionContent = ({
  selectedModels,
  modelData,
  selectedYearsByModel,
  handleYearToggle,
}) => {
  if (selectedModels.length === 0) {
    return (
      <Typography sx={{ fontSize: 14, color: "#888", ml: 1, mt: 5 }}>
        Select a model to see years
      </Typography>
    );
  }

  const modelsToRender = selectedModels.includes("All")
    ? modelData
    : selectedModels
        .filter((modelName) => modelName !== "All")
        .map((modelName) => modelData.find((m) => m.name === modelName))
        .filter(Boolean);

  return (
    <List dense>
      {modelsToRender.map((modelObj) => (
        <Box key={modelObj.name} sx={{ mb: 1 }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: 13,
              color: "#1976d2",
              mb: 0.5,
            }}
          >
            {modelObj.name}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            {modelObj.years.map((year) => (
              <ListItem
                key={year}
                button
                onClick={() => handleYearToggle(modelObj.name, year)}
                sx={{ py: 0.5, pl: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Checkbox
                    edge="start"
                    checked={
                      selectedYearsByModel[modelObj.name]
                        ? selectedYearsByModel[modelObj.name].includes(year)
                        : false
                    }
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: 13,
                  }}
                  primary={year}
                />
              </ListItem>
            ))}
          </Box>
        </Box>
      ))}
    </List>
  );
};

export default function CreateCampaignDialog({
  open,
  onClose,
  ecuTypeData,
  countries,
  clusterNumberData,
  model,
}) {
  const initialCampaignName = "";
  const initialModeldata = [...(model || [])];
  const initialSelectedModels = [];
  const initialSearchModel = "";
  const initialSelectedYears = [];
  const initialMmyInclusions = [];
  const initialSelectedEcuTypes = [];
  const initialEcuTypeInclusions = [];
  const initialSelectedCountries = [];
  const initialSoftwareVersion =
    sessionStorage.getItem("idpLogin") === "true" ? [] : ["ALL"];
  const initialSelectedApps = [];
  const initialFilteredApps = [];
  const initialSearchApp = "";
  const initialStartDate = "";
  const initialEndDate = "";

  // State hooks
  const [campaignName, setCampaignName] = useState(initialCampaignName);

  const [searchModel, setSearchModel] = useState(initialSearchModel);
  const [modelData] = useState(initialModeldata);
  const [selectedModels, setSelectedModels] = useState(initialSelectedModels);
  const [selectedYearsByModel, setSelectedYearsByModel] = useState(initialSelectedYears);
  const [mmyInclusions, setMmyInclusions] = useState(initialMmyInclusions);
  const [selectedEcuTypes, setSelectedEcuTypes] = useState(
    initialSelectedEcuTypes
  );
  const [ecuTypeInclusions, setEcuTypeInclusions] = useState(
    initialEcuTypeInclusions
  );
  const [selectedCountries, setSelectedCountries] = useState(
    initialSelectedCountries
  );
  const [softwareVersion, setSoftwareVersion] = useState(
    initialSoftwareVersion
  );
  const [selectedApps, setSelectedApps] = useState(initialSelectedApps);
  const [filteredApps, setFilteredApps] = useState(initialFilteredApps);
  const [searchApp, setSearchApp] = useState(initialSearchApp);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [campaignNameError, setCampaignNameError] = useState("");
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [searchEcuType, setSearchEcuType] = useState("");
  const [searchSoftwareVersion, setSearchSoftwareVersion] = useState("");
  const [searchCountry, setSearchCountry] = useState("");
  const [isAppSearchEnabled, setIsAppSearchEnabled] = useState(false);

  // Reset all states to initial values
  const handleCloseAndReset = () => {
    setCampaignName(initialCampaignName);
    setCampaignNameError("");
    setSelectedModels(initialSelectedModels);
    setSearchModel(initialSearchModel);
    setSelectedYearsByModel(initialSelectedYears);
    setMmyInclusions(initialMmyInclusions);
    setSelectedEcuTypes(initialSelectedEcuTypes);
    setEcuTypeInclusions(initialEcuTypeInclusions);
    setSelectedCountries(initialSelectedCountries);
    setSoftwareVersion(initialSoftwareVersion);
    setSelectedApps(initialSelectedApps);
    setFilteredApps(initialFilteredApps);
    setSearchApp(initialSearchApp);
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
    setStartDateError("");
    setEndDateError("");
    onClose();
  };
  const dispatch = useDispatch();

  const appsData = useSelector((state) => state.welcomeWizard.appsData);

  // Validate campaign name - only allow alphanumeric, underscore, and hyphen
  const handleCampaignNameChange = (e) => {
    const value = e.target.value;
    setCampaignName(value);
    
    // Regex: only allows letters, numbers, underscore, hyphen, and spaces
    const validPattern = /^[a-zA-Z0-9_\- ]*$/;
    
    if (value && !validPattern.test(value)) {
      setCampaignNameError("Only letters, numbers, underscores (_) and hyphens (-) are allowed");
    } else {
      setCampaignNameError("");
    }
  };

  useEffect(() => {
    console.log("appsData:", appsData);
    if (appsData && appsData.length > 0) {
      setFilteredApps(appsData);
    }
  }, [appsData]);

  const dateStringToMillis = (dateStr, isEndDate = false) => {
    if (!dateStr) return null;

    const date = new Date(dateStr + 'T00:00:00.000Z');

    if (isEndDate) {
      // Set end date to 23:59:59.999 (end of day)
      date.setUTCHours(23, 59, 59, 999);
    } else {
      // Set start date to 00:00:00.000 (start of day)
      date.setUTCHours(0, 0, 0, 0);
    }

    return date.getTime();
  };
  const startDateAdded = ($event) => {
    const newStartDate = $event.target.value;
    setStartDate(newStartDate);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedStartDate = new Date(newStartDate);
    selectedStartDate.setHours(0, 0, 0, 0);

    // Check if start date is before today
    if (selectedStartDate < today) {
      setStartDateError("Start date cannot be earlier than today");
      setEndDateError(""); // Clear end date error
      return;
    }

    // Check if end date exists and start date is after end date
    if (endDate) {
      const selectedEndDate = new Date(endDate);
      selectedEndDate.setHours(0, 0, 0, 0);
      if (selectedStartDate > selectedEndDate) {
        setStartDateError("Start date cannot be greater than end date");
        setEndDateError(""); // Clear end date error
        return;
      }
    }

    setStartDateError("");
    setEndDateError(""); // Clear end date error
  };

  // Update endDateAdded function
  const endDateAdded = ($event) => {
    const newEndDate = $event.target.value;
    setEndDate(newEndDate);

    // End date is optional, so only validate if a value is provided
    if (newEndDate) {
      const selectedEndDate = new Date(newEndDate);
      selectedEndDate.setHours(0, 0, 0, 0);

      // Check if start date exists and end date is before start date
      if (startDate) {
        const selectedStartDate = new Date(startDate);
        selectedStartDate.setHours(0, 0, 0, 0);
        if (selectedEndDate < selectedStartDate) {
          setEndDateError("End date cannot be earlier than start date");
          setStartDateError(""); // Clear start date error
          return;
        }
      }
    }

    setEndDateError("");
    setStartDateError(""); // Clear start date error
  };

  const handleModelToggle = (modelName) => {
    if (modelName === "All") {
      // If selecting "All"
      if (selectedModels.includes("All")) {
        // Unselect all
        setSelectedModels([]);
        setSelectedYearsByModel({});
        setMmyInclusions([]);
      } else {
        // Select all models and all years
        const allModelNames = modelData.map((m) => m.name);
        setSelectedModels(["All", ...allModelNames]);
        const allYearsByModel = {};
        modelData.forEach((m) => {
          allYearsByModel[m.name] = [...m.years];
        });
        setSelectedYearsByModel(allYearsByModel);
        // Set MMY inclusions to <portfolio_id>_ALL_ALL
        const portfolioId = sessionStorage.getItem("portfolioId") || "";
        setMmyInclusions(
          portfolioId === "TML" ? [`TATA_ALL_ALL`] : [`${portfolioId}_ALL_ALL`]
        );
      }
      return;
    }

    // Normal model toggle
    let updatedModels;
    if (selectedModels.includes(modelName)) {
      updatedModels = selectedModels.filter(
        (m) => m !== modelName && m !== "All"
      );
      setSelectedYearsByModel((prev) => {
        const updated = { ...prev };
        delete updated[modelName];
        return updated;
      });
    } else {
      updatedModels = [...selectedModels.filter((m) => m !== "All"), modelName];
    }

    setSelectedModels(updatedModels);
  };

  // Handler for year checkbox
  const handleYearToggle = (modelName, year) => {
    setSelectedYearsByModel((prev) => {
      const years = prev[modelName] || [];
      let updatedYears;
      if (years.includes(year)) {
        updatedYears = years.filter((y) => y !== year);
      } else {
        updatedYears = [...years, year];
      }
      return { ...prev, [modelName]: updatedYears };
    });
  };
  // Handlers

  const handleAppToggle = (app, checked) => {
    console.log("app clicked:", app, checked);
    if (selectedApps.includes(app.packageId)) {
      setSelectedApps(selectedApps.filter((a) => a !== app.packageId));
    } else if (selectedApps.length < 8) {
      setSelectedApps([...selectedApps, app.packageId]);
    }
  };

  const handleEcuTypeChange = (event) => {
    const { value } = event.target;
    const portfolioPrefix = getPortfolioPrefix();
    const newSelected = typeof value === "string" ? value.split(",") : value;
    const allPattern = `${portfolioPrefix}_ALL`;

    // Check if ecuTypeData is empty - only "All" option available
    if (!ecuTypeData || ecuTypeData.length === 0) {
      // Toggle "All" selection
      if (newSelected.includes("All")) {
        setSelectedEcuTypes(["All"]);
        setEcuTypeInclusions([allPattern]);
      } else {
        setSelectedEcuTypes([]);
        setEcuTypeInclusions([]);
      }
      return;
    }

    // Rest of the existing logic for when ecuTypeData has items
    const allWasSelected = selectedEcuTypes.includes("All");
    const allIsSelected = newSelected.includes("All");

    // Case 1: "All" was just selected (wasn't selected before)
    if (allIsSelected && !allWasSelected) {
      const allEcuTypes = ["All", ...ecuTypeData];
      setSelectedEcuTypes(allEcuTypes);
      setEcuTypeInclusions([allPattern]);
      return;
    }

    // Case 2: "All" was just deselected (was selected before but not now)
    if (!allIsSelected && allWasSelected) {
      setSelectedEcuTypes([]);
      setEcuTypeInclusions([]);
      return;
    }

    // Case 3: Individual ECU type was deselected when "All" was selected
    if (
      allWasSelected &&
      allIsSelected &&
      newSelected.length < selectedEcuTypes.length
    ) {
      const filteredSelection = newSelected.filter((item) => item !== "All");
      const newInclusions = mapEcuTypeInclusions(filteredSelection, portfolioPrefix);
      setSelectedEcuTypes(filteredSelection);
      setEcuTypeInclusions(newInclusions);
      return;
    }

    // Case 4: Regular individual selection/deselection (without "All")
    if (
      ecuTypeData &&
      ecuTypeData.length > 0 &&
      newSelected.length === ecuTypeData.length &&
      !allIsSelected
    ) {
      setSelectedEcuTypes(["All", ...newSelected]);
      setEcuTypeInclusions([allPattern]);
    } else {
      const newInclusions = mapEcuTypeInclusions(newSelected, portfolioPrefix);
      setSelectedEcuTypes(newSelected);
      setEcuTypeInclusions(newInclusions);
    }
  };

  const handleSoftwareVersionChange = (event) => {
    const { value } = event.target;
    const newSelected = typeof value === "string" ? value.split(",") : value;

    // Check if "All" was clicked
    const allWasSelected = Array.isArray(softwareVersion)
      ? softwareVersion.includes("All")
      : softwareVersion === "All";
    const allIsSelected = newSelected.includes("All");

    // Case 1: "All" was just selected (wasn't selected before)
    if (allIsSelected && !allWasSelected) {
      // Select all software versions
      const allVersions = ["All", ...clusterNumberData];
      setSoftwareVersion(allVersions);
      return;
    }

    // Case 2: "All" was just deselected (was selected before but not now)
    if (!allIsSelected && allWasSelected) {
      // Deselect all software versions
      setSoftwareVersion([]);
      return;
    }

    // Case 3: Individual software version was deselected when "All" was selected
    if (
      allWasSelected &&
      allIsSelected &&
      newSelected.length <
        (Array.isArray(softwareVersion) ? softwareVersion.length : 1)
    ) {
      // An individual version was deselected, so remove "All"
      const filteredSelection = newSelected.filter((item) => item !== "All");
      setSoftwareVersion(filteredSelection);
      return;
    }

    // Case 4: Regular individual selection/deselection (without "All")
    // If all individual versions are selected (without "All"), treat as all selected
    if (
      clusterNumberData &&
      clusterNumberData.length > 0 &&
      newSelected.length === clusterNumberData.length &&
      !allIsSelected
    ) {
      // All individual versions selected, add "All" automatically
      setSoftwareVersion(["All", ...newSelected]);
    } else {
      // Normal individual selection
      setSoftwareVersion(newSelected);
    }
  };

  const handleCountryChange = (event) => {
    const { value } = event.target;
    const newSelected = typeof value === "string" ? value.split(",") : value;

    // Check if "All" was clicked
    const allWasSelected = selectedCountries.includes("All");
    const allIsSelected = newSelected.includes("All");

    // Case 1: "All" was just selected (wasn't selected before)
    if (allIsSelected && !allWasSelected) {
      // Select all countries
      const allCountries = ["All", ...countries.map((c) => c.iso3Code)];
      setSelectedCountries(allCountries);
      return;
    }

    // Case 2: "All" was just deselected (was selected before but not now)
    if (!allIsSelected && allWasSelected) {
      // Deselect all countries
      setSelectedCountries([]);
      return;
    }

    // Case 3: Individual country was deselected when "All" was selected
    if (
      allWasSelected &&
      allIsSelected &&
      newSelected.length < selectedCountries.length
    ) {
      // An individual country was deselected, so remove "All"
      const filteredSelection = newSelected.filter((item) => item !== "All");
      setSelectedCountries(filteredSelection);
      return;
    }

    // Case 4: Regular individual selection/deselection (without "All")
    // If all individual countries are selected (without "All"), treat as all selected
    if (
      countries &&
      countries.length > 0 &&
      newSelected.length === countries.length &&
      !allIsSelected
    ) {
      // All individual countries selected, add "All" automatically
      setSelectedCountries(["All", ...newSelected]);
    } else {
      // Normal individual selection
      setSelectedCountries(newSelected);
    }
  };

  const getFilteredEcuTypes = () => {
    if (!ecuTypeData) return [];
    return ecuTypeData.filter((type) =>
      type.toLowerCase().includes(searchEcuType.toLowerCase())
    );
  };

  const getFilteredSoftwareVersions = () => {
    if (!clusterNumberData) return [];
    return clusterNumberData.filter((version) =>
      version.toLowerCase().includes(searchSoftwareVersion.toLowerCase())
    );
  };

  const getFilteredCountries = () => {
    if (!countries) return [];
    return countries
      .filter((country) =>
        country.name.toLowerCase().includes(searchCountry.toLowerCase())
      )
      .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name
  };

  useEffect(() => {
    // If "All" is selected, keep <portfolio_id>_ALL_ALL
    if (selectedModels.includes("All")) {
      const portfolioId = sessionStorage.getItem("portfolioId") || "";
      setMmyInclusions(
        portfolioId === "TML" ? [`TATA_ALL_ALL`] : [`${portfolioId}_ALL_ALL`]
      );
      return;
    }
    const portfolioId = sessionStorage.getItem("portfolioId") || "";
    const inclusions = [];
    Object.entries(selectedYearsByModel).forEach(([model, years]) => {
      years.forEach((year) => {
        inclusions.push(`${portfolioId}_${model}_${year}`);
      });
    });
    setMmyInclusions(inclusions);
  }, [selectedYearsByModel, selectedModels]);
  useEffect(() => {
    if (
      selectedEcuTypes.length > 0 &&
      selectedCountries.length > 0 &&
      mmyInclusions.length > 0 &&
      softwareVersion
    ) {
      console.log("selectedEcutypes:", selectedEcuTypes);
      console.log("selectedCountries:", selectedCountries);
      console.log("mmyInclusions:", mmyInclusions);
      console.log("softwareVersion:", softwareVersion);

      const payloadData = {
        tags: [
          {
            key: "ecuType_inclusion",
            values: [...ecuTypeInclusions],
          },
          {
            key: "mmy_inclusion",
            values: [...mmyInclusions],
          },
          {
            key: "clusterNumber",
            values: [...softwareVersion],
          },
          {
            key: "country",
            values: [...selectedCountries],
          },
        ],
      };
      console.log("payload=====>", payloadData);
      dispatch(fetchAppWelcomeWizardStatus(payloadData));
    }
  }, [ecuTypeInclusions, selectedCountries, mmyInclusions, softwareVersion]);

  useEffect(() => {
    // End date is now optional, so removed from required checks
    if (
      selectedEcuTypes.length > 0 &&
      selectedCountries.length > 0 &&
      mmyInclusions.length > 0 &&
      softwareVersion &&
      selectedApps.length > 0 &&
      startDate &&
      campaignName &&
      campaignName.trim() !== "" &&
      !startDateError &&
      !endDateError &&
      !campaignNameError // Also check that there are no campaign name errors
    ) {
      setSaveButtonDisabled(false);
    } else {
      setSaveButtonDisabled(true);
    }
  }, [
    selectedApps,
    endDate,
    startDate,
    campaignName,
    selectedEcuTypes,
    selectedCountries,
    mmyInclusions,
    softwareVersion,
    startDateError,
    endDateError,
    campaignNameError,
  ]);

  const CreateCampaign = () => {
    const tags = [
      {
        key: "ecuType_inclusion",
        values: [...ecuTypeInclusions],
      },
      {
        key: "mmy_inclusion",
        values: [...mmyInclusions],
      },
      {
        key: "clusterNumber",
        values: [...softwareVersion],
      },
      {
        key: "country",
        values: [...selectedCountries],
      },
    ];
    let obj = {};
    obj["name"] = campaignName;
    obj["apps"] = [...selectedApps];
    obj["tags"] = tags;
    obj["startDate"] = dateStringToMillis(startDate, false); // Convert to milliseconds
    if (endDate) {
      obj["endDate"] = dateStringToMillis(endDate, true); // End date at 23:59:59.999
    }
  

    console.log("obj======>", obj);
    dispatch(postCreateWelcomeWizardCampaign(obj));
  };

  useEffect(() => {
    console.log("appsData changed:", appsData);
  }, [appsData]);

  useEffect(() => {
    // Check if all required fields are selected
    const hasEcuType =
      selectedEcuTypes.length > 0 && ecuTypeInclusions.length > 0;
    const hasCountries = selectedCountries.length > 0;
    const hasMmy = mmyInclusions.length > 0;
    const hasSoftwareVersion =
      softwareVersion &&
      (Array.isArray(softwareVersion) ? softwareVersion.length > 0 : true);

    setIsAppSearchEnabled(
      hasEcuType && hasCountries && hasMmy && hasSoftwareVersion
    );
  }, [
    selectedEcuTypes,
    ecuTypeInclusions,
    selectedCountries,
    mmyInclusions,
    softwareVersion,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "22px",
          pb: 0,
          fontFamily: "Roboto",
          fontWeight: 500,
          fontStyle: "Medium",
          letterSpacing: "0%",
          color: "#121415",
        }}
      >
        Create Campaign for Welcome Wizard
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box
          sx={{
            mb: 2,
            marginTop: "10px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            sx={{
              mb: 1,
              fontSize: "16px",
              fontFamily: "Roboto",
              fontWeight: 600,
              fontStyle: "SemiBold",
              lineHeight: "100%",
              letterSpacing: "0px",
              verticalAlign: "middle",
              color: "#2A2C2D",
            }}
          >
            Campaign Name
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter Campaign Name"
            value={campaignName}
            onChange={handleCampaignNameChange}
            error={!!campaignNameError}
            helperText={campaignNameError}
            sx={{ mb: 2 }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          {/* Model selection box */}
          <Box
            sx={{
              flex: 1,
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              background: "#fafafa",
              p: 2,
            }}
          >
            <Typography
              sx={{
                mb: 1,
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: 600,
                fontStyle: "SemiBold",
                letterSpacing: "0px",
                color: "#2A2C2D",
              }}
            >
              Model
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search Model"
              value={searchModel}
              onChange={(e) => setSearchModel(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Box
              sx={{
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              <List dense>
                {/* All option */}
                <ListItem
                  key="All"
                  button
                  onClick={() => handleModelToggle("All")}
                  sx={{ py: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Checkbox
                      edge="start"
                      checked={selectedModels.includes("All")}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      fontFamily: "Roboto",
                      fontWeight: 400,
                      fontSize: 14,
                      letterSpacing: 0.25,
                      color: "#2A2C2D",
                    }}
                    primary="All"
                  />
                </ListItem>
                {/* Filtered models */}
                {modelData
                  .filter((modelItem) =>
                    modelItem.name
                      .toLowerCase()
                      .includes(searchModel.toLowerCase())
                  )
                  .map((modelItem) => (
                    <ListItem
                      key={modelItem.name}
                      button
                      onClick={() => handleModelToggle(modelItem.name)}
                      sx={{ py: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Checkbox
                          edge="start"
                          checked={selectedModels.includes(modelItem.name)}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 400,
                          fontSize: 14,
                          letterSpacing: 0.25,
                          color: "#2A2C2D",
                        }}
                        primary={modelItem.name}
                      />
                    </ListItem>
                  ))}
              </List>
            </Box>
          </Box>

          {/* Year selection box */}
          <Box
            sx={{
              flex: 1,
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              background: "#fafafa",
              p: 2,
            }}
          >
            <Typography
              sx={{
                mb: 1,
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: 600,
                fontStyle: "SemiBold",
                letterSpacing: "0px",
                color: "#2A2C2D",
              }}
            >
              Year
            </Typography>
            <Box
              sx={{
                maxHeight: 248,
                overflowY: "auto",
              }}
            >
              {renderYearSelectionContent({
                selectedModels,
                modelData,
                selectedYearsByModel,
                handleYearToggle,
              })}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "10px",
            gap: 2,
            mb: 2,
          }}
        >
          <FormControl
            sx={{
              flex: 1,
              minWidth:
                sessionStorage.getItem("idpLogin") === "true" ? 250 : "auto",
            }}
          >
            <Typography
              sx={{
                mb: 2,
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: 600,
                fontStyle: "SemiBold",
                letterSpacing: "0px",
                color: "#2A2C2D",
              }}
            >
              ECU Type
            </Typography>
            <Select
              multiple
              value={selectedEcuTypes}
              onChange={handleEcuTypeChange}
              input={<OutlinedInput />}
              renderValue={(selected) => {
                // Case 1: If no ECU types available (empty array) and "All" is selected
                if (
                  (!ecuTypeData || ecuTypeData.length === 0) &&
                  selected.includes("All")
                ) {
                  return "All ECU Types selected";
                }

                const actualSelected = selectedEcuTypes.filter(
                  (s) => s !== "All"
                );

                // Case 2: Nothing selected - show placeholder
                if (actualSelected.length === 0 && !selected.includes("All")) {
                  return (
                    <span style={{ color: "#999" }}>Select ECU Types</span>
                  );
                }

                // Case 3: If "All" is selected (when ecuTypeData has items) - show count
                if (
                  selected.includes("All") &&
                  ecuTypeData &&
                  ecuTypeData.length > 0
                ) {
                  const count = ecuTypeData.length;
                  return `${count} ECU Type${count > 1 ? "s" : ""} selected`;
                }

                // Case 4: Individual selections - show count
                const count = actualSelected.length;
                return `${count} ECU Type${count > 1 ? "s" : ""} selected`;
              }}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
                autoFocus: false,
              }}
              sx={{
                "& .MuiSelect-select": {
                  minHeight: "48px",
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "8px",
                  paddingBottom: "8px",
                },
              }}
            >
              {/* Only show search if there are ECU types available */}
              {ecuTypeData && ecuTypeData.length > 0 && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    position: "sticky",
                    top: 0,
                    bgcolor: "white",
                    zIndex: 1,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search ECU Type"
                    value={searchEcuType}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSearchEcuType(e.target.value);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    fullWidth
                    autoFocus
                  />
                </Box>
              )}

              {/* Always show "All" option */}
              <MenuItem key="All" value="All">
                <Checkbox
                  checked={selectedEcuTypes.includes("All")}
                  size="small"
                />
                <ListItemText primary="All" />
              </MenuItem>

              {/* Show individual ECU types only if available */}
              {ecuTypeData && ecuTypeData.length > 0
                ? getFilteredEcuTypes().map((type) => (
                    <MenuItem key={type} value={type}>
                      <Checkbox
                        checked={selectedEcuTypes.includes(type)}
                        size="small"
                      />
                      <ListItemText primary={type} />
                    </MenuItem>
                  ))
                : null}
            </Select>
          </FormControl>

          {sessionStorage.getItem("idpLogin") === "true" && (
            <FormControl sx={{ flex: 1, minWidth: 250 }}>
              <Typography
                sx={{
                  mb: 2,
                  fontSize: "16px",
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  letterSpacing: "0px",
                  color: "#2A2C2D",
                }}
              >
                Software Version
              </Typography>
              <Select
                multiple
                value={
                  Array.isArray(softwareVersion)
                    ? softwareVersion
                    : [softwareVersion]
                }
                onChange={handleSoftwareVersionChange}
                input={<OutlinedInput />}
                renderValue={(selected) => {
                  const actualSelected = Array.isArray(selected)
                    ? selected.filter((s) => s !== "All")
                    : [];

                  if (actualSelected.length === 0) {
                    return (
                      <span style={{ color: "#999" }}>
                        Select Software Versions
                      </span>
                    );
                  }
                  const count = actualSelected.length;
                  return `${count} Software Version${
                    count > 1 ? "s" : ""
                  } selected`;
                }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                  autoFocus: false,
                }}
                sx={{
                  "& .MuiSelect-select": {
                    minHeight: "48px",
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                }}
              >
                {/* Search TextField inside dropdown */}
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    position: "sticky",
                    top: 0,
                    bgcolor: "white",
                    zIndex: 1,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search Software Version"
                    value={searchSoftwareVersion}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSearchSoftwareVersion(e.target.value);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    fullWidth
                    autoFocus
                  />
                </Box>
                {clusterNumberData && clusterNumberData.length > 0 ? (
                  [
                    // All option first
                    <MenuItem key="All" value="All">
                      <Checkbox
                        checked={
                          Array.isArray(softwareVersion)
                            ? softwareVersion.includes("All")
                            : softwareVersion === "All"
                        }
                        size="small"
                      />
                      <ListItemText primary="All" />
                    </MenuItem>,
                    // Then filtered software versions
                    ...getFilteredSoftwareVersions().map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        <Checkbox
                          checked={
                            Array.isArray(softwareVersion)
                              ? softwareVersion.includes(opt)
                              : softwareVersion === opt
                          }
                          size="small"
                        />
                        <ListItemText primary={opt} />
                      </MenuItem>
                    )),
                  ]
                ) : (
                  <MenuItem disabled>
                    <ListItemText primary="No software versions Available" />
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}

          <FormControl
            sx={{
              flex: 1,
              minWidth:
                sessionStorage.getItem("idpLogin") === "true" ? 250 : "auto",
            }}
          >
            <Typography
              sx={{
                mb: 2,
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: 600,
                fontStyle: "SemiBold",
                letterSpacing: "0px",
                color: "#2A2C2D",
              }}
            >
              Countries
            </Typography>
            <Select
              multiple
              value={selectedCountries}
              onChange={handleCountryChange}
              input={<OutlinedInput />}
              renderValue={(selected) => {
                const actualSelected = selected.filter((s) => s !== "All");

                if (actualSelected.length === 0) {
                  return (
                    <span style={{ color: "#999" }}>Select Countries</span>
                  );
                }
                const count = actualSelected.length;
                return `${count} Countr${count > 1 ? "ies" : "y"} selected`;
              }}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
                autoFocus: false,
              }}
              sx={{
                "& .MuiSelect-select": {
                  minHeight: "48px",
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "8px",
                  paddingBottom: "8px",
                },
              }}
            >
              {/* Search TextField inside dropdown */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  position: "sticky",
                  top: 0,
                  bgcolor: "white",
                  zIndex: 1,
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search Country"
                  value={searchCountry}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchCountry(e.target.value);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  fullWidth
                  autoFocus
                />
              </Box>
              {countries && countries.length > 0 ? (
                [
                  // All option first
                  <MenuItem key="All" value="All">
                    <Checkbox
                      checked={selectedCountries.includes("All")}
                      size="small"
                    />
                    <ListItemText primary="All" />
                  </MenuItem>,
                  // Then filtered countries
                  ...getFilteredCountries().map((country) => (
                    <MenuItem key={country.iso3Code} value={country.iso3Code}>
                      <Checkbox
                        checked={selectedCountries.includes(country.iso3Code)}
                        size="small"
                      />
                      <ListItemText primary={country.name} />
                    </MenuItem>
                  )),
                ]
              ) : (
                <MenuItem disabled>
                  <ListItemText primary="No Countries Available" />
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
        {/* Apps Multi-select with search and chips */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              mb: 1,
              fontSize: "16px",
              fontFamily: "Roboto",
              fontWeight: 600,
              fontStyle: "SemiBold",
              letterSpacing: "0px",
              color: "#2A2C2D",
            }}
          >
            Apps (max 8 apps)
          </Typography>
          <TextField
            fullWidth
            placeholder={
              isAppSearchEnabled
                ? "Search for App"
                : "Please select Model, ECU Type, Software Version and Countries first"
            }
            value={searchApp}
            onChange={(e) => setSearchApp(e.target.value)}
            sx={{ mb: 1 }}
            disabled={!isAppSearchEnabled}
          />
          <Box
            sx={{
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              maxHeight: 120,
              overflowY: "auto",
              background: isAppSearchEnabled ? "#fafafa" : "#f5f5f5",
              opacity: isAppSearchEnabled ? 1 : 0.6,
            }}
          >
            {renderAppsSelectionContent({
              isAppSearchEnabled,
              filteredApps,
              searchApp,
              selectedApps,
              handleAppToggle,
              appsData,
            })}
          </Box>
        </Box>
        {/* Start/End Date */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1, width: "100%" }}>
            <Typography
              sx={{
                mb: 2,
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: 600,
                fontStyle: "SemiBold",
                letterSpacing: "0px",
                color: "#2A2C2D",
              }}
            >
              Start date
            </Typography>
            <TextField
              type="date"
              value={startDate}
              onChange={(e) => startDateAdded(e)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: "100%" }}
              error={!!startDateError}
              helperText={startDateError}
            />
          </Box>
          <Box sx={{ flex: 1, width: "100%" }}>
            <Typography
              sx={{
                mb: 2,
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: 600,
                fontStyle: "SemiBold",
                letterSpacing: "0px",
                color: "#2A2C2D",
              }}
            >
              End date{" "}
              <span style={{ color: "#888", fontWeight: 400 }}>(Optional)</span>
            </Typography>
            <TextField
              type="date"
              value={endDate}
              onChange={(e) => endDateAdded(e)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: "100%" }}
              error={!!endDateError}
              helperText={endDateError}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", pb: 2 }}>
        <Button
          onClick={handleCloseAndReset}
          variant="outlined"
          sx={{
            fontFamily: "Roboto",
            fontWeight: 500,
            fontStyle: "Medium",
            fontSize: 14,
            letterSpacing: "0%",
            background: "#f5f5f5",
            color: "#222",
            borderRadius: "6px",
            textTransform: "none",
            px: 4,
            mr: 1,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            CreateCampaign(); // Save logic here
            onClose();
          }}
          variant="contained"
          sx={{
            ml: 2,
            background: "#0070f3",
            fontFamily: "Roboto",
            fontWeight: 500,
            fontStyle: "Medium",
            fontSize: 14,
            letterSpacing: "0%",
            borderRadius: "6px",
            textTransform: "none",
            px: 4,
          }}
          disabled={saveButtonDisabled}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CreateCampaignDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  ecuTypeData: PropTypes.arrayOf(PropTypes.string),
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      iso3Code: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  clusterNumberData: PropTypes.arrayOf(PropTypes.string),
  model: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      years: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      ),
    })
  ),
};
